// "Someone is looking at the top cards of this pile."
//
// When a player looks at the top cards of a deck or discard pile, both players see it on the pile: a ring
// of light swells round it, one ghostly card lifts off the top for each card being looked at, fanned out
// in a rising stack, and an eye opens above them. The more cards, the bigger all of it is and the longer
// it lasts. Colors are in src/config.ts under playEffects.colors.pileLook; sizes under pileLook.
import * as THREE from 'three';
import { CONFIG } from '../../config.ts';

const CW = CONFIG.card.width;
const CH = CONFIG.card.height;

let shared: { ring: THREE.Texture; ghost: THREE.Texture; eye: THREE.Texture; geo: THREE.PlaneGeometry; ringGeo: THREE.PlaneGeometry } | null = null;
function assets() {
  if (shared) return shared;
  const canvas = (w: number, h: number) => {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return [c, c.getContext('2d')!] as const;
  };
  // a soft ring
  const [rc, r] = canvas(256, 256);
  const g = r.createRadialGradient(128, 128, 60, 128, 128, 126);
  g.addColorStop(0, 'rgba(255,255,255,0)');
  g.addColorStop(0.62, 'rgba(255,255,255,0.75)');
  g.addColorStop(0.78, 'rgba(255,255,255,0.28)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  r.fillStyle = g;
  r.fillRect(0, 0, 256, 256);
  // a ghost card: a bright rounded outline with a faint fill
  const [gc, c] = canvas(200, 280);
  c.beginPath();
  c.roundRect(8, 8, 184, 264, 18);
  c.fillStyle = 'rgba(255,255,255,0.07)';
  c.fill();
  c.lineWidth = 9;
  c.strokeStyle = 'rgba(255,255,255,0.95)';
  c.stroke();
  // an eye: two arcs for the lids, an iris and a pupil
  const [ec, e] = canvas(256, 256);
  e.lineWidth = 12;
  e.lineCap = 'round';
  e.strokeStyle = '#fff';
  e.fillStyle = 'rgba(20,24,34,0.7)';
  e.beginPath();
  e.moveTo(24, 128);
  e.quadraticCurveTo(128, 20, 232, 128);
  e.quadraticCurveTo(128, 236, 24, 128);
  e.closePath();
  e.fill();
  e.stroke();
  e.beginPath();
  e.arc(128, 128, 40, 0, Math.PI * 2);
  e.fillStyle = '#fff';
  e.fill();
  e.beginPath();
  e.arc(128, 128, 17, 0, Math.PI * 2);
  e.fillStyle = 'rgba(14,16,24,0.95)';
  e.fill();
  const geo = new THREE.PlaneGeometry(CW, CH);
  geo.rotateX(-Math.PI / 2);
  const ringGeo = new THREE.PlaneGeometry(2, 2);
  ringGeo.rotateX(-Math.PI / 2);
  shared = { ring: new THREE.CanvasTexture(rc), ghost: new THREE.CanvasTexture(gc), eye: new THREE.CanvasTexture(ec), geo, ringGeo };
  return shared;
}

interface Look {
  group: THREE.Group;
  ring: THREE.Mesh;
  ghosts: { m: THREE.Mesh; rise: number; start: number; turn: number }[];
  eye: THREE.Sprite;
  born: number;
  length: number;
  n: number; // how many ghost cards
  top: number; // how high the tallest ghost goes
}

export class PileLooks {
  private scene: THREE.Scene;
  private live: Look[] = [];
  private now = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /**
   * @param at    the middle of the pile, on the table
   * @param topY  how tall the pile stands right now
   * @param yaw   which way the pile faces
   * @param count how many cards are being looked at
   */
  play(at: THREE.Vector3, topY: number, yaw: number, count: number) {
    const L = CONFIG.pileLook;
    const colors = CONFIG.playEffects.colors.pileLook;
    if (!L.enabled || !CONFIG.playEffects.enabled || !colors?.length) return;
    const a = assets();
    const n = Math.max(1, Math.min(L.mostCards, Math.round(count)));
    const group = new THREE.Group();
    group.position.set(at.x, topY, at.z);
    group.rotation.y = yaw;
    const mat = (map: THREE.Texture, color: string) => new THREE.MeshBasicMaterial({ map, color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending });
    const ring = new THREE.Mesh(a.ringGeo, mat(a.ring, colors[0]));
    ring.position.y = 0.08 - topY + 0.05; // on the table round the foot of the pile
    ring.renderOrder = 16;
    group.add(ring);
    const ghosts: Look['ghosts'] = [];
    for (let i = 0; i < n; i++) {
      const m = new THREE.Mesh(a.geo, mat(a.ghost, colors[Math.min(1, colors.length - 1)]));
      m.renderOrder = 17 + i;
      group.add(m);
      // each one a little higher than the last, fanned to either side like a hand of cards
      ghosts.push({ m, rise: L.firstRise + i * L.step, start: i * L.stagger, turn: (i - (n - 1) / 2) * L.fan });
    }
    const top = L.firstRise + (n - 1) * L.step;
    const eye = new THREE.Sprite(new THREE.SpriteMaterial({ map: a.eye, color: colors[0], transparent: true, opacity: 0, depthTest: false, depthWrite: false }));
    eye.renderOrder = 42;
    group.add(eye);
    this.scene.add(group);
    this.live.push({ group, ring, ghosts, eye, born: this.now, length: L.seconds + n * L.secondsPerCard, n, top });
  }

  /** `time` in seconds. */
  update(time: number) {
    this.now = time;
    const L = CONFIG.pileLook;
    this.live = this.live.filter((look) => {
      const t = time - look.born;
      const k = t / look.length;
      if (k >= 1) {
        this.remove(look);
        return false;
      }
      const out = k > 0.75 ? 1 - (k - 0.75) / 0.25 : 1; // everything fades over the last quarter
      const big = 1 + (look.n - 1) * L.growPerCard; // the more cards, the bigger
      // the ring: swells out, then breathes
      const swell = Math.min(1, t / 0.35);
      const r = (CH * 0.78 + look.n * L.ringPerCard) * (0.5 + 0.5 * (1 - (1 - swell) ** 3)) * (1 + 0.05 * Math.sin(time * 5));
      look.ring.scale.set(r, 1, r);
      (look.ring.material as THREE.MeshBasicMaterial).opacity = 0.8 * out * swell;
      // the ghost cards lift off one after another
      for (const g of look.ghosts) {
        const q = Math.min(1, Math.max(0, (t - g.start) / 0.45));
        const e = 1 - (1 - q) ** 3;
        g.m.position.y = 0.15 + g.rise * e + 0.12 * Math.sin(time * 2.2 + g.rise);
        g.m.rotation.y = g.turn * e;
        g.m.position.x = Math.sin(g.turn) * CW * 0.9 * e;
        // many cards overlap: each one is fainter, so the stack reads as cards and not as one white block
        (g.m.material as THREE.MeshBasicMaterial).opacity = (0.8 / (1 + 0.16 * (look.n - 1))) * q * out;
      }
      // the eye opens above the topmost one once they are up, blinks once, and closes as it all fades
      const openAt = Math.min(0.5, look.n * L.stagger);
      const open = Math.min(1, Math.max(0, (t - openAt) / 0.22));
      const blinkAt = look.length * 0.5;
      const blink = Math.abs(t - blinkAt) < 0.09 ? Math.abs(t - blinkAt) / 0.09 : 1;
      const size = L.eye * big;
      look.eye.position.y = look.top + 1.2 + size * 0.4;
      look.eye.scale.set(size, size * Math.max(0.06, open * blink * (0.25 + 0.75 * out)), 1);
      look.eye.material.opacity = open * out;
      return true;
    });
  }

  private remove(look: Look) {
    this.scene.remove(look.group);
    (look.ring.material as THREE.Material).dispose();
    for (const g of look.ghosts) (g.m.material as THREE.Material).dispose();
    look.eye.material.dispose();
  }

  dispose() {
    for (const look of this.live) this.remove(look);
    this.live = [];
  }
}
